import { useEffect, useRef, useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

export interface RealtimeEvent {
  event: string
  tenant_slug: string
  timestamp: string
  data: Record<string, unknown>
}

export interface UseRealtimeEventsOptions {
  /** Function that returns the current JWT access token */
  getAccessToken: () => string | null
  /** Callback fired for every incoming event */
  onEvent?: (event: RealtimeEvent) => void
  /** Map of event type -> React Query keys to invalidate */
  invalidationMap?: Record<string, string[][]>
  /** Whether the hook is enabled (e.g. only when authenticated) */
  enabled?: boolean
}

const DEFAULT_INVALIDATION_MAP: Record<string, string[][]> = {
  reading: [['readings']],
  sensor_status: [['sensors'], ['readings']],
  notification: [['notifications']],
  stats: [['readings', 'stats']],
}

const MIN_RECONNECT_DELAY = 1000
const MAX_RECONNECT_DELAY = 30000

function getWsUrl(token: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`
}

export function useRealtimeEvents({
  getAccessToken,
  onEvent,
  invalidationMap = DEFAULT_INVALIDATION_MAP,
  enabled = true,
}: UseRealtimeEventsOptions) {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected')
  const queryClient = useQueryClient()
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectDelay = useRef(MIN_RECONNECT_DELAY)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)

  const cleanup = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current)
      reconnectTimer.current = null
    }
    if (wsRef.current) {
      wsRef.current.onopen = null
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      wsRef.current.onmessage = null
      wsRef.current.close()
      wsRef.current = null
    }
  }, [])

  const connect = useCallback(() => {
    if (!mountedRef.current || !enabled) return

    const token = getAccessToken()
    if (!token) {
      setStatus('disconnected')
      return
    }

    cleanup()
    setStatus('connecting')

    const ws = new WebSocket(getWsUrl(token))
    wsRef.current = ws

    ws.onopen = () => {
      if (!mountedRef.current) return
      setStatus('connected')
      reconnectDelay.current = MIN_RECONNECT_DELAY
    }

    ws.onmessage = (e) => {
      if (!mountedRef.current) return
      try {
        const event: RealtimeEvent = JSON.parse(e.data)
        onEvent?.(event)

        // Invalidate React Query caches based on event type
        const keys = invalidationMap[event.event]
        if (keys) {
          for (const key of keys) {
            queryClient.invalidateQueries({ queryKey: key })
          }
        }
      } catch {
        // Ignore malformed messages
      }
    }

    ws.onclose = (e) => {
      if (!mountedRef.current) return
      wsRef.current = null

      if (e.code === 4001) {
        // Token expired — try to reconnect immediately (token may have been refreshed)
        setStatus('disconnected')
        reconnectTimer.current = setTimeout(connect, 500)
        return
      }

      setStatus('disconnected')
      // Exponential backoff reconnect
      reconnectTimer.current = setTimeout(() => {
        reconnectDelay.current = Math.min(reconnectDelay.current * 2, MAX_RECONNECT_DELAY)
        connect()
      }, reconnectDelay.current)
    }

    ws.onerror = () => {
      if (!mountedRef.current) return
      setStatus('error')
    }
  }, [enabled, getAccessToken, onEvent, invalidationMap, queryClient, cleanup])

  // Main effect: connect/disconnect based on enabled state
  useEffect(() => {
    mountedRef.current = true
    if (enabled) {
      connect()
    } else {
      cleanup()
      setStatus('disconnected')
    }
    return () => {
      mountedRef.current = false
      cleanup()
    }
  }, [enabled, connect, cleanup])

  // Listen for token refresh events → reconnect with new token
  useEffect(() => {
    const handler = () => {
      if (enabled && mountedRef.current) {
        connect()
      }
    }
    window.addEventListener('intellimon:token-refreshed', handler)
    return () => window.removeEventListener('intellimon:token-refreshed', handler)
  }, [enabled, connect])

  return { status }
}
